import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Activity,
  CheckCircle2,
  ClipboardList,
  Clock,
  FileWarning,
  Paperclip,
  Users,
} from "lucide-react";
import { getDashboardStats } from "../api/reports";
import { ActivityCard } from "../components/ActivityCard";
import { Alert, Button, Card, EmptyState, PageHeader, Spinner, StatCard } from "../components/ui";
import { useAuth } from "../hooks/useAuth";
import { formatMonth, percentage, roleLabel } from "../lib/format";

export function DashboardPage() {
  const { user } = useAuth();
  const statsQuery = useQuery({
    queryKey: ["dashboard", "stats"],
    queryFn: getDashboardStats,
  });

  if (statsQuery.isLoading) return <Spinner label="Memuat dasbor..." />;
  if (statsQuery.isError || !statsQuery.data) {
    return <Alert tone="error">Dasbor tidak dapat dimuat. Coba muat ulang halaman.</Alert>;
  }

  const stats = statsQuery.data;
  const maxTrend = Math.max(1, ...stats.monthlyTrend.map((point) => point.count));

  return (
    <div className="space-y-5">
      <PageHeader
        title={`Selamat datang, ${user?.fullName ?? ""}`}
        description={user ? `Masuk sebagai ${roleLabel(user.role)}.` : undefined}
        actions={
          <Link to="/kegiatan/baru">
            <Button>Catat kegiatan</Button>
          </Link>
        }
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="Total kegiatan" value={stats.totalActivities} />
        <StatCard
          label="Menunggu validasi"
          value={stats.pendingValidation}
          tone="warning"
          hint="Perlu ditelaah"
        />
        <StatCard label="Disetujui" value={stats.approvedActivities} tone="success" />
        <StatCard label="Ditolak" value={stats.rejectedActivities} tone="danger" />
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="Draf" value={stats.draftActivities} />
        <StatCard label="Diarsipkan" value={stats.archivedActivities} />
        <StatCard label="Bukti dukung" value={stats.evidenceCount} />
        <StatCard label="Peserta tercatat" value={stats.participantCount} />
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <Card className="space-y-3">
          <h2 className="flex items-center gap-2 font-semibold text-slate-800">
            <Activity className="size-4 text-brand-600" aria-hidden />
            Distribusi kategori
          </h2>
          {stats.categoryDistribution.length === 0 ? (
            <EmptyState title="Belum ada kegiatan pada lingkup Anda" />
          ) : (
            <ul className="space-y-3">
              {stats.categoryDistribution.slice(0, 8).map((row) => (
                <li key={row.categoryId} className="space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-700">{row.categoryName}</span>
                    <span className="font-semibold text-slate-800">{row.count}</span>
                  </div>
                  <div className="h-2 rounded-full bg-slate-100">
                    <div
                      className="h-2 rounded-full bg-brand-500"
                      style={{ width: `${percentage(row.count, stats.totalActivities)}%` }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="space-y-3">
          <h2 className="flex items-center gap-2 font-semibold text-slate-800">
            <ClipboardList className="size-4 text-brand-600" aria-hidden />
            Tren 6 bulan terakhir
          </h2>
          <ul className="flex h-40 items-end gap-2">
            {stats.monthlyTrend.map((point) => (
              <li key={point.month} className="flex flex-1 flex-col items-center gap-1">
                <span className="text-xs font-semibold text-slate-600">{point.count}</span>
                <div
                  className="w-full rounded-t-lg bg-brand-500/80"
                  style={{ height: `${Math.max(4, (point.count / maxTrend) * 100)}%` }}
                  title={`${point.count} kegiatan`}
                />
                <span className="text-[10px] text-slate-500">{formatMonth(point.month)}</span>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      <Card className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 font-semibold text-slate-800">
            <Clock className="size-4 text-brand-600" aria-hidden />
            Kegiatan terbaru
          </h2>
          <Link to="/kegiatan" className="text-sm font-medium text-brand-700 hover:underline">
            Lihat semua
          </Link>
        </div>
        {stats.recentActivities.length === 0 ? (
          <EmptyState
            title="Belum ada kegiatan"
            description="Mulai dengan mencatat kegiatan pertama pada lingkup Anda."
            action={
              <Link to="/kegiatan/baru">
                <Button>Catat kegiatan</Button>
              </Link>
            }
          />
        ) : (
          <ul className="space-y-3">
            {stats.recentActivities.map((activity) => (
              <li key={activity.id}>
                <ActivityCard activity={activity} />
              </li>
            ))}
          </ul>
        )}
      </Card>

      <div className="grid gap-3 sm:grid-cols-3">
        <SummaryTile
          icon={<CheckCircle2 className="size-4" aria-hidden />}
          label="Tervalidasi"
          value={stats.approvedActivities}
        />
        <SummaryTile
          icon={<FileWarning className="size-4" aria-hidden />}
          label="Ditolak"
          value={stats.rejectedActivities}
        />
        <SummaryTile
          icon={<Paperclip className="size-4" aria-hidden />}
          label="Bukti tersimpan"
          value={stats.evidenceCount}
        />
      </div>
      <p className="flex items-center gap-2 text-xs text-slate-500">
        <Users className="size-4" aria-hidden />
        Angka di atas hanya mencakup data dalam lingkup wewenang Anda.
      </p>
    </div>
  );
}

function SummaryTile({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
}) {
  return (
    <div className="flex items-center gap-3 rounded-2xl bg-white px-4 py-3 ring-1 ring-slate-200">
      <span className="rounded-xl bg-brand-50 p-2 text-brand-700">{icon}</span>
      <span>
        <span className="block text-xs text-slate-500">{label}</span>
        <span className="block text-lg font-semibold text-slate-800">{value}</span>
      </span>
    </div>
  );
}
